import type {
  FlowNodeData,
  FlowNodeMcpInfo,
  MediaToolsModelConfig,
  MediaToolsModelToolConfig,
  ModelToolParameterItem,
  ModelToolParameterType,
} from '$/domains/workFlowV3/type';
import useTeamFlowStore, { setCommonModelMcpInfoMap } from '$/store/useTeamFlowStore';
import type { FlowNodeEntity } from '@flowgram.ai/free-layout-editor';
import {
  NODE_TYPE_TO_MODEL_TYPE_MAP,
  TVFlowNodeNextTypeEnum,
  type ModelNodeType,
} from '../config/node.config';
import {
  deriveInputTypeCounts,
  getMediaModelsByNodeType,
  isToolCompatible,
  resolveAutoMatchedNodeData,
} from './autoMatchModelTool';
import {
  getDefaultParams,
  getInitialNodeMcpData,
  getModelById,
  getToolById,
} from './modelConfig';

/**
 * 获取节点类型默认的模型配置
 *
 * @param {TVFlowNodeNextTypeEnum} nodeType
 * @return {*}
 */
function getDefaultModelConfig(nodeType: TVFlowNodeNextTypeEnum) {
  const { modelConfig } = useTeamFlowStore.getState();
  const modelType = NODE_TYPE_TO_MODEL_TYPE_MAP[nodeType as keyof typeof NODE_TYPE_TO_MODEL_TYPE_MAP];
  const modelList = modelConfig[modelType];
  let model: MediaToolsModelConfig | null = null;
  let tool: MediaToolsModelToolConfig | null = null;
  for (const modelItem of modelList) {
    const _tool = modelItem.tool_list.find((toolItem) => {
      const isMatch = Object.keys(toolItem.input_parameters).some((pKey) => {
        const param = toolItem.input_parameters[pKey];
        if (param.type === 'text') {
          return true;
        }
        return false;
      });
      return isMatch;
    });
    if (_tool && !model) {
      model = modelItem;
      tool = _tool;
    }
  }

  if (model && tool) {
    return {
      model_id: model.model_id,
      node_model_id: tool.node_model_id,
      api_name: tool.api_name,
      parameters: getDefaultParams(tool.parameters),
    };
  }
  return null;
}

/**
 * 设置所有节点类型的通用模型参数
 *
 * @export
 */
export function setAllTypeCommonModelMcpInfo() {
  const modelTypes = Object.keys(NODE_TYPE_TO_MODEL_TYPE_MAP);
  modelTypes.forEach((modelType) => {
    const defaultModelMcpInfo = getDefaultModelConfig(modelType as TVFlowNodeNextTypeEnum);
    if (defaultModelMcpInfo) {
      setCommonModelMcpInfoMap(modelType as TVFlowNodeNextTypeEnum, defaultModelMcpInfo);
    }
  });
}

const SYNCABLE_PARAMETER_TYPES: ModelToolParameterType[] = ['select', 'ratio', 'slider', 'count', 'switch'];

/**
 * 判断参数是否可从公共配置继承；prompt 等节点级输入始终取默认值。
 *
 * @param {ModelToolParameterItem | undefined} config
 * @return {boolean}
 */
function shouldInheritCommonParameter(config: ModelToolParameterItem | undefined): boolean {
  if (!config?.type || config.type === 'prompt' || config.type === 'timbre') {
    return false;
  }
  return SYNCABLE_PARAMETER_TYPES.includes(config.type);
}

/**
 * 判断公共参数值是否在当前参数配置的可选范围内。
 *
 * @param {ModelToolParameterItem} config
 * @param {unknown} value
 * @return {boolean}
 */
function isCommonParameterValueSupported(
  config: ModelToolParameterItem,
  value: unknown,
): boolean {
  if (typeof value === 'undefined' || value === null) {
    return false;
  }

  const {
    type, options, min, max,
  } = config;

  if (type === 'select' || type === 'ratio' || type === 'switch') {
    if (options?.length) {
      return options.some((option) => String(option.value) === String(value));
    }
    return true;
  }

  if (type === 'slider' || type === 'count') {
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
      return false;
    }
    if (typeof min === 'number' && numericValue < min) {
      return false;
    }
    if (typeof max === 'number' && numericValue > max) {
      return false;
    }
    return true;
  }

  return false;
}

/**
 * 获取可同步参数在无法继承公共参数时的回退值。
 * 公共字段存在但 slider 值不受支持时取最大值；字段缺失时取配置默认值。
 *
 * @param {ModelToolParameterItem} config
 * @param {Record<string, string | number | boolean>} syncableDefaultParameters
 * @param {string} key
 * @param {boolean} hasCommonParameter
 * @return {string | number | boolean | undefined}
 */
function getSyncableParameterFallbackValue(
  config: ModelToolParameterItem,
  syncableDefaultParameters: Record<string, string | number | boolean>,
  key: string,
  hasCommonParameter: boolean,
): string | number | boolean | undefined {
  if (hasCommonParameter && config.type === 'slider' && typeof config.max === 'number') {
    return config.max;
  }

  if (typeof syncableDefaultParameters[key] !== 'undefined') {
    return syncableDefaultParameters[key];
  }

  if (typeof config.default !== 'undefined') {
    return config.default;
  }

  return undefined;
}

/**
 * 按新节点参数配置构建 parameters，并优先继承同类型节点的公共参数。
 * 仅同步 select / ratio / slider / count / switch 类型，且公共参数值需在新配置中有效。
 * slider 类型公共值超出目标范围时回退为最大值；公共字段缺失时回退为配置 default。
 * prompt 等节点级输入不参与公共参数继承，始终取配置 default，无 default 时传空字符串。
 * 其余不可同步类型同样取配置 default，无 default 时传空字符串。
 *
 * @param {TVFlowNodeNextTypeEnum} nodeType
 * @param {Record<string, ModelToolParameterItem>} nextNodeParametersConfig
 * @param {FlowNodeMcpInfo['parameters'] | undefined} commonParameters 本次创建前缓存的参数，未传时读取当前公共缓存
 * @return {Record<string, string | number | boolean>}
 */
export function inheritCommonParameters(
  nodeType: TVFlowNodeNextTypeEnum,
  nextNodeParametersConfig: Record<string, ModelToolParameterItem>,
  commonParameters = useTeamFlowStore.getState().commonModelMcpInfoMap[nodeType]?.parameters,
): Record<string, string | number | boolean> {
  const syncableParametersConfig = Object.fromEntries(
    Object.entries(nextNodeParametersConfig).filter(([, config]) => (
      shouldInheritCommonParameter(config)
    )),
  );
  const syncableDefaultParameters = getDefaultParams(syncableParametersConfig);
  const allDefaultParameters = getDefaultParams(nextNodeParametersConfig);
  const parameters: Record<string, string | number | boolean> = {};

  Object.entries(nextNodeParametersConfig).forEach(([key, config]) => {
    if (!shouldInheritCommonParameter(config)) {
      if (typeof allDefaultParameters[key] !== 'undefined') {
        parameters[key] = allDefaultParameters[key];
      }
      return;
    }

    const hasCommonParameter = Boolean(
      commonParameters && Object.prototype.hasOwnProperty.call(commonParameters, key),
    );
    const commonParameterValue = commonParameters?.[key];
    if (
      hasCommonParameter
      && typeof commonParameterValue !== 'undefined'
      && isCommonParameterValueSupported(config, commonParameterValue)
    ) {
      parameters[key] = commonParameterValue;
      return;
    }

    const fallbackValue = getSyncableParameterFallbackValue(
      config,
      syncableDefaultParameters,
      key,
      hasCommonParameter,
    );
    if (typeof fallbackValue !== 'undefined') {
      parameters[key] = fallbackValue;
    }
  });

  return parameters;
}

/**
 * 读取节点类型对应的模型列表。
 *
 * @param {TVFlowNodeNextTypeEnum} nodeType
 * @return {MediaToolsModelConfig[]}
 */
function getModelsByNodeType(nodeType: TVFlowNodeNextTypeEnum): MediaToolsModelConfig[] {
  const modelType = NODE_TYPE_TO_MODEL_TYPE_MAP[nodeType as ModelNodeType];
  const { modelConfig } = useTeamFlowStore.getState();
  const mediaModels = getMediaModelsByNodeType(nodeType, modelConfig);
  if (mediaModels?.length) {
    return mediaModels;
  }
  const models = modelConfig?.[modelType];
  return Array.isArray(models) ? models : [];
}

/**
 * 在目标模型的 tool_list 中按 node_model_id 与 api_name 精确查找工具。
 *
 * @param {MediaToolsModelConfig | undefined} model
 * @param {string} nodeModelId
 * @param {string} apiName
 * @return {MediaToolsModelToolConfig | undefined}
 */
function findToolInModel(
  model: MediaToolsModelConfig | undefined,
  nodeModelId: string,
  apiName: string,
): MediaToolsModelToolConfig | undefined {
  if (!model?.tool_list?.length) {
    return undefined;
  }
  return model.tool_list.find(
    (tool) => tool.node_model_id === nodeModelId,
  );
}

/**
 * 判断切换目标模型后是否可沿用公共参数中的 tool。
 * 需同时满足：目标模型存在对应 tool，且当前节点连入满足该 tool 的 input_parameters 要求。
 *
 * @param {FlowNodeEntity} node
 * @param {MediaToolsModelConfig} targetModel
 * @return {MediaToolsModelToolConfig | undefined}
 */
export function inheritCommonTool(
  node: FlowNodeEntity,
  targetModel: MediaToolsModelConfig,
): MediaToolsModelToolConfig | undefined {
  const nodeType = node.flowNodeType as TVFlowNodeNextTypeEnum;
  const commonModelMcpInfo = useTeamFlowStore.getState().commonModelMcpInfoMap[nodeType];

  // 1. 公共参数中需已记录完整的 tool 标识（node_model_id + api_name）
  if (!commonModelMcpInfo?.node_model_id || !commonModelMcpInfo?.api_name) {
    return undefined;
  }

  const commonTool = findToolInModel(
    targetModel,
    commonModelMcpInfo.node_model_id,
    commonModelMcpInfo.api_name,
  );

  // 2. 目标模型的 tool_list 中需存在与公共参数一致的工具
  if (!commonTool) {
    return undefined;
  }

  const nodeData = (node.toJSON()?.data ?? {}) as FlowNodeData;
  const inputCounts = deriveInputTypeCounts(nodeData.inputNodeId);

  // 3. 当前节点连入需满足公共 tool 的 input_parameters 要求（如图文生视频需有图片连入）
  if (!isToolCompatible(commonTool, inputCounts)) {
    return undefined;
  }

  return commonTool;
}

export interface InheritCommonMcpInfoParams {
  node: FlowNodeEntity;
  targetModelId: string;
}

/**
 * 按节点类型返回新建节点时的默认 mcpInfo。
 *
 * @param {TVFlowNodeNextTypeEnum} nodeType
 * @return {FlowNodeMcpInfo | undefined}
 */
function getFallbackInitialMcpInfo(nodeType: TVFlowNodeNextTypeEnum): FlowNodeMcpInfo | undefined {
  const modelType = NODE_TYPE_TO_MODEL_TYPE_MAP[nodeType as ModelNodeType];
  const { modelConfig } = useTeamFlowStore.getState();
  return getInitialNodeMcpData(modelConfig?.[modelType])?.mcpInfo;
}

/**
 * 切换目标模型时构建 mcpInfo：优先继承公共 tool 与 parameters，否则按连入自动匹配 tool。
 *
 * @param {InheritCommonMcpInfoParams} params
 * @return {FlowNodeMcpInfo | undefined}
 */
export function inheritCommonMcpInfo({
  node,
  targetModelId,
}: InheritCommonMcpInfoParams): FlowNodeMcpInfo | undefined {
  const nodeType = node.flowNodeType as TVFlowNodeNextTypeEnum;
  const modelType = NODE_TYPE_TO_MODEL_TYPE_MAP[nodeType as ModelNodeType];
  const commonModelMcpInfo = useTeamFlowStore.getState().commonModelMcpInfoMap[nodeType];
  const targetModel = getModelById(modelType, targetModelId);

  // 1. 目标模型不存在时，回退到该节点类型的默认 mcpInfo
  if (!targetModel) {
    return getFallbackInitialMcpInfo(nodeType);
  }

  const commonTool = inheritCommonTool(node, targetModel);

  // 2. 公共 tool 可用时，沿用公共 tool，parameters 按配置合并公共参数（prompt 取默认值）
  if (commonTool && commonModelMcpInfo) {
    return {
      model_id: targetModelId,
      node_model_id: commonTool.node_model_id,
      api_name: commonTool.api_name,
      parameters: inheritCommonParameters(nodeType, commonTool.parameters),
    };
  }

  // 3. 公共 tool 不可用时，在目标模型内按当前连入自动匹配兼容 tool
  const nodeData = (node.toJSON()?.data ?? {}) as FlowNodeData;
  const models = getModelsByNodeType(nodeType);
  const matchedData = resolveAutoMatchedNodeData(
    node,
    nodeData,
    models,
    nodeData.inputNodeId,
    { fixedModelId: targetModelId },
  );

  // 4. 自动匹配失败时，同样回退到默认 mcpInfo
  if (!matchedData?.mcpInfo) {
    return getFallbackInitialMcpInfo(nodeType);
  }

  const matchedTool = findToolInModel(
    targetModel,
    matchedData.mcpInfo.node_model_id,
    matchedData.mcpInfo.api_name,
  ) ?? getToolById(
    targetModel,
    modelType,
    matchedData.mcpInfo.node_model_id,
    matchedData.mcpInfo.api_name,
  );

  // 5. 使用自动匹配到的 tool，parameters 合并公共参数（prompt 取默认值）
  return {
    model_id: targetModelId,
    node_model_id: matchedData.mcpInfo.node_model_id,
    api_name: matchedData.mcpInfo.api_name,
    parameters: inheritCommonParameters(nodeType, matchedTool?.parameters ?? {}),
  };
}
